import React, {ReactNode} from 'react';
import {View, TouchableOpacity, StyleSheet, Platform, ViewStyle} from 'react-native';
import {DrawerActions, useNavigation} from '@react-navigation/native';
import {Header, StackNavigationProp, StackHeaderLeftButtonProps, StackHeaderTitleProps} from '@react-navigation/stack';

import {RootMobileTitle} from '../components/rootMobileTitle';
import {Screens} from './model';

import {useTheme} from '../theme';
import {Theme} from '../theme/interface';
import {images} from '../../assets/icons';
import {useStyles} from '../hooks/styles';
import {NoInternetNotification} from '../components/noInternetNotification';
import {CardBlockedNotification} from '../components/cardBlockedNotification';

const leftButtonStyle: ViewStyle = {
  width: 44,
  height: 44,
  alignItems: 'center',
  justifyContent: 'center',
};

const Burger = (props: {navigation: StackNavigationProp<any, string>}) => {
  const theme = useTheme();
  const styles = useStyles(() => ({
    button: leftButtonStyle,
  }));
  return (
    <TouchableOpacity
      testID='topbar-burger-btn-id'
      style={styles.button}
      onPress={() => props.navigation.dispatch(DrawerActions.toggleDrawer)}>
      <images.Burger height={24} width={24} color={theme.color.primaryColor} />
    </TouchableOpacity>
  );
};

const CustomHeaderBackButton = (props: StackHeaderLeftButtonProps) => {
  const theme = useTheme();
  const styles = StyleSheet.create({
    root: {
      marginHorizontal: 0,
      ...leftButtonStyle,
    },
  });
  return (
    <TouchableOpacity {...props} style={styles.root} testID='header-content'>
      <images.Left height={24} width={24} color={theme.color.primaryColor} />
    </TouchableOpacity>
  );
};

const RightSpacer = () => {
  const styles = StyleSheet.create({
    root: {
      height: 24,
      width: 24,
    },
  });
  return <View style={styles.root} />;
};

export const createCommonOptions = (
  theme: Theme,
  title: string | string[],
  isChild: boolean,
  titleNumberOfLines?: number,
) => ({
  title: Array.isArray(title) ? title.join('\n') : title,
  header: (props: any) => (
    <View testID='header-container' style={{flexDirection: 'column', alignItems: 'stretch'}}>
      <Header {...props} />
      <NoInternetNotification />
      <CardBlockedNotification />
    </View>
  ),
  headerBackTitleVisible: false,
  headerTitle: (props: StackHeaderTitleProps) => (
    <RootMobileTitle
      testID='header-content'
      align={isChild ? 'left' : 'center'}
      title={title}
      {...props}
      numberOfLines={titleNumberOfLines}
    />
  ),
  headerTitleContainerStyle: {
    flexShrink: 1,
    flexGrow: 1,
    ...Platform.select<ViewStyle>({
      ios: isChild ? {left: 30} : {maxWidth: '70%'},
      android: isChild ? {left: 56} : {maxWidth: '100%'},
      default: {left: 56, right: 44},
    }),
  },
  headerRight: () => <RightSpacer />,
  headerRightContainerStyle: {paddingHorizontal: 10},
  headerStyle: {
    ...theme.topBar.header,
    height: 54,
  },
  headerStatusBarHeight: 0,
});

export const createRootScreenOptions = (
  theme: Theme,
  title: string | string[],
  navigation: StackNavigationProp<any, string>,
) => ({
  ...createCommonOptions(theme, title, false),
  headerLeft: () => null,
  headerRight: () => (Platform.OS === 'web' ? <Burger navigation={navigation} /> : null),
  headerLeftContainerStyle: {paddingHorizontal: 7},
});

export const createChildScreenOptions = (
  theme: Theme,
  title: string,
  navigation: StackNavigationProp<any, string>,
  backScreen?: Screens,
  right?: ReactNode,
  titleNumberOfLines?: number,
) => {
  const Right = right as any;
  const handleBackPress = () => {
    navigation.navigate(backScreen!);
  };

  return {
    ...createCommonOptions(theme, title, true, titleNumberOfLines),
    headerLeft: (props: StackHeaderLeftButtonProps) => (
      <CustomHeaderBackButton {...props} onPress={backScreen ? handleBackPress : props.onPress} />
    ),
    headerRight: (props: StackHeaderLeftButtonProps) => (Right ? <Right {...props} /> : null),
    headerLeftContainerStyle: {paddingHorizontal: 7},
  };
};

const ProfileHeaderBackButton = (props: StackHeaderLeftButtonProps & {backScreen?: Screens}) => {
  const navigation = useNavigation();
  const theme = useTheme();

  const styles = StyleSheet.create({
    root: {
      marginHorizontal: 0,
    },
  });

  const handleBack = () => {
    navigation.navigate(props.backScreen || Screens.ProfileMenu);
  };

  return (
    <TouchableOpacity testID='nadleBack-btn-id' style={styles.root} onPress={handleBack}>
      <images.Left height={24} width={24} color={theme.color.primaryColor} />
    </TouchableOpacity>
  );
};

export const createProfileChildScreenOptions = (
  theme: Theme,
  title: string,
  backScreen?: Screens,
  right?: ReactNode,
) => {
  const Right = right as any;
  return {
    ...createCommonOptions(theme, title, true),
    headerLeft: (props: StackHeaderLeftButtonProps) => <ProfileHeaderBackButton {...props} backScreen={backScreen} />,
    headerRight: (props: StackHeaderLeftButtonProps) => (Right ? <Right {...props} /> : null),
    headerLeftContainerStyle: {paddingHorizontal: 7},
  };
};
